/* eslint-disable @typescript-eslint/no-misused-promises */
import {Link} from "react-router-dom";
import {z} from "zod";
import {useSetAtom} from "jotai";
import {SubmitHandler, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {zodResolver} from "@hookform/resolvers/zod";
import {Alert, Box, Grid, Paper, Stack, Typography} from "@mui/material";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {userLogin, resendVerificationEmail} from "../../services/authenticationService";
import {
    getResourceError,
    getResourceErrorMessage,
    isResourceFailed,
    isResourceLoading,
} from "../../utils/resourceUtils";
import {swallowException} from "../../utils/appUtils";
import {authenticationAtom} from "../../store/authenticationStore";
import {LabeledInput} from "../base/LabeledInput";
import {ActionButton} from "../base/ActionButton";
import {AppLayout} from "../AppLayout";
import {EmptyHeader} from "../EmptyHeader";
import {TextInput} from "../base/TextInput";
import {CognitoErrorReason, ResourceErrorType} from "../../types/resourceError";
import {FailedResource} from "../../types/resource";

export const LoginPage: React.FC = () => {
    const {t} = useTranslation();
    const {
        reset,
        resource,
        userLogin: login,
        resendVerificationEmail: resendEmail,
    } = useResourceManagement({userLogin, resendVerificationEmail});
    const refreshAuth = useSetAtom(authenticationAtom);
    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm<LoginSchema>({
        resolver: zodResolver(LOGIN_SCHEMA),
    });

    const onSubmit: SubmitHandler<LoginSchema> = (data) => {
        reset();

        login({username: data.email, password: data.password})
            .then(() => refreshAuth())
            .catch(swallowException);
    };

    const onResendConfirmationEmail: SubmitHandler<LoginSchema> = (data) => {
        reset();

        resendEmail(data.email).catch(swallowException);
    };

    return (
        <AppLayout header={<EmptyHeader />}>
            <Grid container component="main" display="flex" justifyContent="center" alignItems="center" height="100%">
                <Grid
                    item
                    component={Paper}
                    elevation={0}
                    square
                    sx={{
                        minWidth: "31.25rem",
                        width: "31.25rem",
                        borderRadius: "1rem",
                    }}
                >
                    {isResourceFailed(resource) && (
                        <Alert severity="error" sx={{paddingX: "2.5rem"}}>
                            {getResourceErrorMessage(resource)}
                            {isEmailNotConfirmedResourceError(resource) && (
                                <>
                                    <br />
                                    <a href=" #" onClick={handleSubmit(onResendConfirmationEmail)}>
                                        {t("login_page.resend_confirmation")}
                                    </a>
                                </>
                            )}
                        </Alert>
                    )}
                    <Stack
                        direction="column"
                        justifyContent="center"
                        sx={{
                            paddingY: "2rem",
                            paddingX: "3rem",
                        }}
                    >
                        <Stack direction="column" alignItems="center">
                            <Typography component="h1" variant="h5" data-test="header">
                                {t("login_page.title")}
                            </Typography>
                            <Box sx={{marginTop: "1rem", paddingX: "1.25rem", width: "100%"}}>
                                <form>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <LabeledInput label={t("login_page.form.email")}>
                                                <TextInput
                                                    name="email"
                                                    type="text"
                                                    data-test="email"
                                                    register={register}
                                                    errors={errors}
                                                    inputProps={{autoFocus: true, autoComplete: "username"}}
                                                />
                                            </LabeledInput>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <LabeledInput label={t("login_page.form.password")}>
                                                <TextInput
                                                    name="password"
                                                    type="password"
                                                    data-test="password"
                                                    register={register}
                                                    errors={errors}
                                                    inputProps={{autoComplete: "current-password"}}
                                                />
                                            </LabeledInput>
                                            <Stack direction="row" justifyContent="end">
                                                <Link to="/forgot-password" style={{textDecoration: "none"}}>
                                                    {t("login_page.form.forgot_password")}
                                                </Link>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                    <ActionButton
                                        type="submit"
                                        data-test="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        icon="none"
                                        onClick={handleSubmit(onSubmit)}
                                        disabled={isResourceLoading(resource)}
                                        sx={{
                                            marginTop: "1.5rem",
                                            marginBottom: "1rem",
                                        }}
                                    >
                                        {t("login_page.form.submit_button")}
                                    </ActionButton>
                                    <Typography textAlign="center">
                                        {t("login_page.form.register1")}{" "}
                                        <Link to="/register" style={{textDecoration: "none"}} data-test="register">
                                            {t("login_page.form.register2")}
                                        </Link>
                                    </Typography>
                                </form>
                            </Box>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </AppLayout>
    );
};

const LOGIN_SCHEMA = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});

type LoginSchema = z.infer<typeof LOGIN_SCHEMA>;

const isEmailNotConfirmedResourceError = (resource: FailedResource<unknown>) => {
    const error = getResourceError(resource);
    return error?.type === ResourceErrorType.COGNITO_ERROR && error?.reason === CognitoErrorReason.EMAIL_NOT_CONFIRMED;
};
